import { useCallback } from "react";
import { z } from "zod";
import type { Attachment } from "@/shared/types/entries";
import { useApi } from "@/shared/utils/request";
import { actingHeaders } from "@/v2/hooks/actions/types";

export type UploadProgress = (loaded: number, total: number) => void;

/**
 * Raw PUT of the file bytes straight to Azure Blob Storage via the SAS url.
 *
 * Uses XHR (not fetch) for upload-progress events. Deliberately bypasses
 * `makeApiRequest` — the SAS url is pre-authorized, and sending our WorkOS
 * bearer token to Azure would make it reject the request.
 */
function putToBlob(
	uploadUrl: string,
	file: File,
	onProgress?: UploadProgress,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("PUT", uploadUrl);
		xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
		if (file.type) xhr.setRequestHeader("Content-Type", file.type);
		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) onProgress?.(e.loaded, e.total);
		};
		xhr.onload = () =>
			xhr.status >= 200 && xhr.status < 300
				? resolve()
				: reject(new Error(`Upload failed (${xhr.status})`));
		xhr.onerror = () => reject(new Error("Upload failed"));
		xhr.send(file);
	});
}

const attachmentSchema = z.object({
	id: z.string(),
	fileName: z.string(),
	contentType: z.string(),
	size: z.number(),
	status: z.enum(["pending", "uploaded"]),
	createdAt: z.string(),
});

const uploadTicketSchema = z.object({
	attachment: attachmentSchema,
	uploadUrl: z.string(),
});

export function useAttachmentUpload() {
	const { makeApiRequest } = useApi();

	/**
	 * Request a write SAS for `entryId` (the `ent_<n>` form), PUT the bytes to
	 * Azure, and return the new attachment id. There is no confirmation call —
	 * the row is reconciled against blob existence on the next read.
	 */
	const uploadFile = useCallback(
		async (
			entryId: string,
			file: File,
			opts?: { timekeeperId?: string | null; onProgress?: UploadProgress },
		): Promise<Attachment> => {
			const resp = await makeApiRequest({
				endpoint: `/v2/entries/${entryId}/attachments`,
				method: "POST",
				body: {
					fileName: file.name,
					contentType: file.type,
					size: file.size,
				},
				headers: actingHeaders(opts?.timekeeperId),
			});
			if (resp.status === "ERROR") {
				throw new Error(resp.error || "Failed to start upload");
			}
			const parsed = uploadTicketSchema.safeParse(resp.data);
			if (!parsed.success) {
				throw new Error("Malformed upload ticket");
			}
			const ticket = parsed.data;
			await putToBlob(ticket.uploadUrl, file, opts?.onProgress);
			return { ...ticket.attachment, status: "uploaded" };
		},
		[makeApiRequest],
	);

	return { uploadFile };
}
